import { Edit, HelpOutline, InfoOutlined } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Button,
  FormControl,
  FormControlLabel,
  Grid2,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  Autocomplete,
  Fade,
  FormLabel,
  List,
  ListItemText,
  ListItem,
  Collapse,
  Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import {
  BankAccount,
  Contact,
  Department,
  Invoice,
  PatchInvoiceInput,
  RevenueType,
  State,
  Tax
} from '@thro-types/billing';
import { ShibUser } from '@thro-types/users';

import { endOfDay, isValid } from 'date-fns';
import { FormikErrors, useFormik, useFormikContext } from 'formik';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useGetDepartmentsQuery } from 'api/hooks/billing/department';
import { useInvoiceMutation } from 'api/hooks/billing/invoices';
import { useGetRevenueTypesQuery } from 'api/hooks/billing/revenueType';
import { SimplePDFViewer } from 'app/components/PdfPreview';
import { UserSelectionModal } from 'app/components/UserSelectionModal';
import {
  AdditionalInvoiceState,
  ApproveDialog,
  ApproveInvoicesButton,
  ApproveSplitDialog,
  CancelDialog,
  CancelInvoicesButton,
  DeleteDialog,
  DeleteInvoiceButton,
  DenyDialog,
  DenyInvoicesButton,
  InfoCustomerCard,
  InvoiceStateChip,
  InvoiceStateWithAdditional,
  MarkAsReadyButton,
  MarkedAsReady,
  OpenInDMSButton,
  PdfPreview,
  ToCanceledInvoiceButton,
  ToOriginalnvoiceButton
} from 'app/containers/Billing/components';
import { useFetchPdf } from 'app/containers/Billing/hooks';
import {
  canCreateInvoices,
  canEditInvoice,
  hasDifferentTaxVersions,
  isCreatorOfInvoice,
  isFinanceUser,
  shouldRenderActionButtons
} from 'app/containers/Billing/utils';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { FEATURE_FLAGS } from 'utils/featureFlags';
import { useAuthContext } from 'utils/hooks/useAuthContext';

import { ArticleConfiguration, Ref } from '../../components/ArticleConfiguration';
import { invoiceReadyValidationSchema } from '../validation';

interface Props {
  invoice: Invoice;
  isCancelledInvoice: boolean;
}

interface ExpertContact extends Pick<Contact, 'firstName' | 'lastName' | 'email' | 'uid'> {}

export const DetailBody = ({ invoice, isCancelledInvoice }: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { values, setFieldValue, setFieldTouched } = useFormikContext<
    PatchInvoiceInput & {
      salestax: Tax;
      expertContact: ExpertContact;
      revenueType: RevenueType;
      department: Department;
    }
  >();
  const [showUserSelectionModal, setShowUserSelectionModal] = useState(false);
  const [denyDialogOpen, setDenyDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approveSplitDialogOpen, setApproveSplitDialogOpen] = useState(false);
  const locationState = location.state as { editMode: boolean } | undefined;
  const [editMode, setEditMode] = useState(locationState?.editMode ?? false);

  const [shouldShowReferenceError, setShouldShowReferenceError] = useState(false);

  const { user, permissions } = useAuthContext();
  const { data: revenueTypes, isLoading } = useGetRevenueTypesQuery();
  const { data: departments, isLoading: isLoadingDepartments } = useGetDepartmentsQuery();

  const { updateState, update, cancel, deletion } = useInvoiceMutation();

  const articleConfigurationRef = useRef<Ref>(null);

  const { pdfData, open, handleClose, handlePreview } = useFetchPdf();

  const isPreviewEnabled = useBooleanFlagValue(FEATURE_FLAGS.BILLING.INVOICEPREVIEW_ENABLED, false);

  const {
    errors,
    validateForm,
    isValid: isValidForm
  } = useFormik({
    initialValues: invoice,
    validationSchema: invoiceReadyValidationSchema(t),
    onSubmit: () => {}
  });

  useEffect(() => {
    validateForm(invoice);
  }, [invoice, validateForm]);

  const handleChange = async (name: string, value: unknown) => {
    await setFieldValue(name, value);
    setFieldTouched(name, true);
  };

  const handleUserSelection = (user: ShibUser) => {
    const expertContact: ExpertContact = {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      uid: user.uid
    };

    update.mutateAsync({
      id: invoice.id,
      data: { expertContact: expertContact }
    });

    handleChange('expertContact', expertContact);
    setShowUserSelectionModal(false);
  };

  const handleMarkAsReadyButtonClick = () => {
    if (hasDifferentTaxVersions(invoice)) {
      setApproveSplitDialogOpen(true);
    } else {
      markAsReady();
    }
  };

  const markAsReady = useCallback(async () => {
    if (invoice) {
      await updateState.mutateAsync({
        invoiceIds: [invoice.id],
        state: 'READY'
      });
    }
  }, [updateState, invoice]);

  // const handleTaxChange = (event: SelectChangeEvent) => {
  //   update.mutateAsync({ id: invoice.id, data: { salestaxVersionId: event.target.value } });
  // };

  const handleEditModeSwitch = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setEditMode(checked);
    // Used to reset the editingArticle in the articleConfiguration when leaving edit mode
    articleConfigurationRef.current?.resetEditingArticle();
  };

  const denyInvoice = async () => {
    try {
      await update.mutateAsync({
        id: invoice.id,
        data: { cancellationReason: values.cancellationReason }
      });
      await updateState.mutateAsync({ invoiceIds: [invoice.id], state: 'DENIED' });
    } catch (error) {
      dispatch(AlertActions.error(t(translations.pages.billingFinance.mutation.denyFailed())));
    } finally {
      setDenyDialogOpen(false);
    }
  };

  const handleApproveInvoice = async () => {
    try {
      await updateState.mutateAsync({
        invoiceIds: [invoice.id],
        state: 'APPROVED'
      });
      dispatch(
        AlertActions.success(t(translations.pages.billingFinance.mutation.approveSuccessful()))
      );
    } catch {
      dispatch(AlertActions.error(t(translations.pages.billingFinance.mutation.approveFailed())));
    } finally {
      setApproveDialogOpen(false);
    }
  };

  const handleSplitInvoice = async () => {
    try {
      await markAsReady();
      dispatch(
        AlertActions.success(t(translations.pages.billingFinance.mutation.splitInvoiceSuccessful()))
      );
      navigate('/billing?tab=open');
    } catch {
      dispatch(
        AlertActions.error(t(translations.pages.billingFinance.mutation.splitInvoiceFailed()))
      );
    } finally {
      setApproveSplitDialogOpen(false);
    }
  };

  const handleCancelInvoice = async () => {
    try {
      await cancel.mutateAsync(invoice.id);
      dispatch(
        AlertActions.success(t(translations.pages.billingFinance.mutation.cancelSuccessful()))
      );
    } catch {
      dispatch(AlertActions.error(t(translations.pages.billingFinance.mutation.cancelFailed())));
    } finally {
      setCancelDialogOpen(false);
    }
  };

  const handleDeleteInvoice = async () => {
    try {
      await deletion.mutateAsync(invoice.id);
      dispatch(
        AlertActions.success(t(translations.pages.billingFinance.mutation.deleteSuccessful()))
      );
      navigate('/billing');
    } catch {
      dispatch(AlertActions.error(t(translations.pages.billingFinance.mutation.deleteFailed())));
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const openDenyDialog = () => {
    setDenyDialogOpen(true);
  };

  const openCancelDialog = () => {
    setCancelDialogOpen(true);
  };

  const openDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  const getStateForChip = (state: InvoiceStateWithAdditional) => {
    if (state === State.READY && !isValidForm) return AdditionalInvoiceState.INCOMPLETE;
    return state;
  };

  const ActionButtons = () => {
    switch (invoice.state) {
      case 'DRAFT':
        return isCreatorOfInvoice(user, invoice) ? (
          <>
            <MarkAsReadyButton
              onClick={handleMarkAsReadyButtonClick}
              disabled={invoice.state !== 'DRAFT' || !isValidForm || editMode}
            />
            <DeleteInvoiceButton onClick={openDeleteDialog} />
          </>
        ) : (
          <></>
        );
      case 'READY':
        return (
          <>
            {isFinanceUser(user, permissions) ? (
              <>
                <ApproveInvoicesButton
                  onClick={() => setApproveDialogOpen(true)}
                  disabled={!isValidForm || editMode}
                />
                <DenyInvoicesButton onClick={openDenyDialog} disabled={editMode} />
                {isCancelledInvoice && (
                  <Link to={`/billing/invoices/${invoice.originalInvoice?.id}`}>
                    <ToOriginalnvoiceButton />
                  </Link>
                )}
              </>
            ) : (
              <>
                <MarkedAsReady />
                <DeleteInvoiceButton onClick={openDeleteDialog} />
              </>
            )}
          </>
        );

      case 'APPROVED':
      case 'FINAL':
      case 'ARCHIVED':
        return (
          <>
            {isCancelledInvoice ? (
              <Link to={`/billing/invoices/${invoice.originalInvoice?.id}`}>
                <ToOriginalnvoiceButton />
              </Link>
            ) : (
              <CancelInvoicesButton
                onClick={openCancelDialog}
                disabled={editMode || !!invoice.cancelledInvoiceId}
              />
            )}
            {invoice.cancelledInvoiceId && (
              <Link to={`/billing/invoices/${invoice.cancelledInvoiceId}`}>
                <ToCanceledInvoiceButton />
              </Link>
            )}
            {invoice.dmsLink && (
              <Link target="_blank" to={invoice.dmsLink}>
                <OpenInDMSButton />
              </Link>
            )}
          </>
        );
      default:
        return null;
    }
  };

  if (!invoice) {
    return null;
  }

  const handleAdditionalRecipientChange = (_: React.SyntheticEvent, value: string[]) => {
    handleChange('additionalRecipients', value);
    update.mutateAsync({
      id: invoice.id,
      data: { additionalRecipients: value }
    });
  };

  return (
    <div style={{ marginBottom: 20 }}>
      {pdfData && open && (
        <SimplePDFViewer pdfFilePath={pdfData} open={!!open} onClose={handleClose} />
      )}
      {invoice.state === 'DENIED' && isCreatorOfInvoice(user, invoice) && (
        <Alert
          severity="error"
          style={{ marginBottom: 10 }}
          action={
            <Button
              variant="contained"
              size="small"
              onClick={() => updateState.mutateAsync({ invoiceIds: [invoice.id], state: 'DRAFT' })}
            >
              {t(translations.pages.billing.editInvoice())}
            </Button>
          }
        >
          <AlertTitle>
            {invoice.cancellationReason
              ? t(translations.pages.billing.alerts.invoiceDeniedText(), {
                  reason: invoice.cancellationReason
                })
              : t(translations.pages.billing.alerts.invoiceDeniedWithoutReason())}
          </AlertTitle>
        </Alert>
      )}
      <Grid2 container gap={1} justifyContent={'space-between'} marginBottom={2}>
        <Grid2 container gap={1} justifyContent={'flex-start'} flexDirection={'column'} size={4}>
          <FormControl required disabled style={{ gap: 3 }}>
            <FormLabel sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <Typography variant="h6">{t(translations.common.customer())}</Typography>
            </FormLabel>
            <TextField
              value={
                invoice.customer?.companyName ||
                `${invoice.customer?.firstName} ${invoice.customer?.lastName}`
              }
              disabled
              size="small"
            />
          </FormControl>
          <FormControl size="small" disabled={!editMode} style={{ gap: 3 }}>
            <FormLabel sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <Typography variant="h6">
                {t(translations.pages.billing.detail.yourReference())}
              </Typography>
            </FormLabel>
            <TextField
              value={values.yourReference}
              disabled={!editMode}
              size="small"
              helperText={shouldShowReferenceError && t(translations.validation.max(), { max: 12 })}
              onChange={e => {
                if (e.currentTarget.value?.length > 12) {
                  handleChange('yourReference', e.target.value.slice(0, 12));
                  setShouldShowReferenceError(true);
                }
                if (e.currentTarget.value?.length <= 12) {
                  handleChange('yourReference', e.target.value);
                  setShouldShowReferenceError(false);
                }
              }}
              slotProps={{ htmlInput: { maxLength: 13 } }}
              onBlur={() => {
                update.mutateAsync({
                  id: invoice.id,
                  data: { yourReference: values.yourReference }
                });
              }}
            />
          </FormControl>
          <FormControl size="small" disabled={!editMode} required style={{ gap: 3 }}>
            <FormLabel sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <Typography variant="h6">
                {t(translations.pages.billing.detail.expertContact())}
              </Typography>
            </FormLabel>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setShowUserSelectionModal(true)}
              endIcon={<Edit />}
              sx={{
                justifyContent: 'space-between',
                opacity: editMode ? 0.7 : 1,
                borderColor: 'darkgray',
                '&.Mui-disabled': { borderColor: 'darkgray' }
              }}
              disabled={!editMode}
            >
              {values.expertContact
                ? `${values.expertContact?.firstName} ${values.expertContact?.lastName}`
                : t(translations.common.select())}
            </Button>
            <UserSelectionModal
              open={showUserSelectionModal}
              setOpen={() => setShowUserSelectionModal(false)}
              handleSubmit={handleUserSelection}
              hideMtrNumberField
              hideUidField
            />
          </FormControl>
          <Grid2 container gap={2}>
            <FormControl size="small" disabled={!editMode} style={{ gap: 3 }} fullWidth>
              <FormLabel sx={{ display: 'inline-flex', alignItems: 'center' }}>
                <Typography variant="h6">
                  {t(translations.pages.billing.detail.department())}
                </Typography>
              </FormLabel>
              <Autocomplete
                options={departments?.data || []}
                getOptionLabel={department => `${department.name} (${department.email})`}
                renderInput={params => <TextField {...params} />}
                size="small"
                onChange={(event, value) => {
                  handleChange('departmentVersionId', value?.id);
                  handleChange('department', value);
                  update.mutateAsync({
                    id: invoice.id,
                    data: { departmentVersionId: value?.id || null }
                  });
                }}
                style={{ minWidth: '20em' }}
                value={values.department || null}
                disabled={!editMode}
                loading={isLoadingDepartments}
              />
            </FormControl>
            <Collapse in={!!values.department} unmountOnExit sx={{ width: '100%' }}>
              <Tooltip
                title={t(translations.pages.billing.tooltips.departmentsCanOnlyBeChangedByAdmin())}
                placement="right"
              >
                <Grid2 container justifyContent={'space-between'} gap={2} size={'grow'}>
                  <Grid2 size="grow">
                    <TextField
                      size="small"
                      disabled
                      value={values.department?.email}
                      label={t(translations.common.email())}
                      fullWidth
                    />
                  </Grid2>
                  <Grid2 size="grow">
                    <TextField
                      size="small"
                      disabled
                      value={values.department?.phone}
                      label={t(translations.common.phone())}
                      fullWidth
                    />
                  </Grid2>
                </Grid2>
              </Tooltip>
            </Collapse>
          </Grid2>
          <FormControl size="small" disabled={!editMode} style={{ gap: 6 }}>
            <FormLabel
              sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Typography variant="h6">
                {t(translations.pages.billing.detail.additionalRecipients())}
              </Typography>
              <Tooltip title={t(translations.pages.billing.tooltips.additionalRecipients())}>
                <InfoOutlined />
              </Tooltip>
            </FormLabel>
            <Autocomplete
              clearIcon={false}
              multiple
              freeSolo
              value={values.additionalRecipients}
              onChange={handleAdditionalRecipientChange}
              renderInput={params => <TextField {...params} />}
              size="small"
              options={[]}
              disabled={!editMode}
            />
          </FormControl>
          <FormControl size="small" disabled={!editMode} required style={{ gap: 6 }}>
            <FormLabel sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <Typography variant="h6">{t(translations.pages.billing.detail.details())}</Typography>
            </FormLabel>
            <Grid2
              justifyContent={'space-between'}
              display={'flex'}
              alignItems={'center'}
              gap={2}
              justifyItems={'strech'}
            >
              <FormControl size="small" disabled={!editMode} fullWidth>
                <InputLabel>{t(translations.common.bankAccount())}</InputLabel>
                <Select
                  label={t(translations.common.bankAccount())}
                  value={values.bankAccount}
                  onChange={e => {
                    handleChange('bankAccount', e.target.value);
                    let reminderKey = '';
                    switch (e.target.value) {
                      case BankAccount.STOK:
                        handleChange('reminderKey', '14');
                        reminderKey = '14';
                        break;
                      case BankAccount.SPARKASSE:
                        handleChange('reminderKey', '');
                        break;
                      default:
                    }
                    update.mutate({
                      id: invoice.id,
                      data: {
                        bankAccount: e.target.value as BankAccount,
                        reminderKey: reminderKey
                      }
                    });
                  }}
                >
                  <MenuItem value={BankAccount.STOK}>{BankAccount.STOK}</MenuItem>
                  <MenuItem value={BankAccount.SPARKASSE}>{BankAccount.SPARKASSE}</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" disabled={!editMode} fullWidth>
                <InputLabel>{t(translations.common.paymentTarget())}</InputLabel>
                <Select
                  label={t(translations.common.paymentTarget())}
                  value={values.paymentTarget || ''}
                  onChange={e => {
                    handleChange('paymentTarget', e.target.value);
                    update.mutate({ id: invoice.id, data: { paymentTarget: e.target.value } });
                  }}
                >
                  <MenuItem value="0d">
                    {t(translations.pages.billing.detail.paymentTargetNow())}
                  </MenuItem>
                  <MenuItem value="14d">
                    {t(translations.pages.billing.detail.paymentTarget(), { target: '14' })}
                  </MenuItem>
                  <MenuItem value="30d">
                    {t(translations.pages.billing.detail.paymentTarget(), { target: '30' })}
                  </MenuItem>
                  <MenuItem value="60d">
                    {t(translations.pages.billing.detail.paymentTarget(), { target: '60' })}
                  </MenuItem>
                  <MenuItem value="90d">
                    {t(translations.pages.billing.detail.paymentTarget(), { target: '90' })}
                  </MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" disabled={!editMode} fullWidth>
                <InputLabel>{t(translations.pages.billing.detail.reminderKey())}</InputLabel>
                <Select
                  label={t(translations.pages.billing.detail.reminderKey())}
                  value={values.reminderKey}
                  disabled={
                    !editMode ||
                    !isFinanceUser(user, permissions) ||
                    invoice.bankAccount === BankAccount.SPARKASSE
                  }
                  onChange={e => {
                    if (values.bankAccount === BankAccount.STOK) {
                      handleChange('reminderKey', e.target.value);
                      update.mutate({
                        id: invoice.id,
                        data: { reminderKey: e.target.value }
                      });
                    }
                  }}
                >
                  <MenuItem value={'14'}>14</MenuItem>
                  <MenuItem value={'13'}>13</MenuItem>
                </Select>
              </FormControl>
            </Grid2>
          </FormControl>
          <FormControl size="small" disabled={!editMode} required style={{ gap: 3 }}>
            <FormLabel sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <Typography variant="h6">
                {t(translations.pages.billing.invoiceCreate.invoiceDate())}
              </Typography>
            </FormLabel>
            <DatePicker
              renderInput={params => <TextField {...params} size="small" />}
              value={values.invoiceDate}
              onChange={date => {
                if (!date) {
                  return;
                }
                handleChange('invoiceDate', date);
                update.mutateAsync({
                  id: invoice.id,
                  data: { invoiceDate: date }
                });
              }}
              inputFormat="dd.MM.yyyy"
              disabled={!editMode}
            />
          </FormControl>
          <Grid2 container gap={2}>
            <FormControl size="small" disabled={!editMode} fullWidth style={{ gap: 3 }}>
              <FormLabel sx={{ display: 'inline-flex', alignItems: 'center' }}>
                <Typography variant="h6">
                  {t(translations.pages.billing.detail.servicePeriod())}
                </Typography>
              </FormLabel>
              <Grid2 container gap={2}>
                <Grid2 size="grow">
                  <DatePicker
                    onChange={date => {
                      if (isValid(date)) {
                        const validDate = date as Date;
                        handleChange('servicePeriodStart', validDate);
                        update.mutateAsync({
                          id: invoice.id,
                          data: { servicePeriodStart: endOfDay(validDate) }
                        });
                      } else if (date === null) {
                        handleChange('servicePeriodStart', date);
                        update.mutateAsync({
                          id: invoice.id,
                          data: { servicePeriodStart: date }
                        });
                      }
                    }}
                    value={values.servicePeriodStart}
                    renderInput={props => <TextField {...props} size="small" />}
                    disabled={!editMode}
                    inputFormat="dd.MM.yyyy"
                  />
                </Grid2>
                <Grid2>
                  <Typography variant="h6" style={{ alignSelf: 'center' }}>
                    -
                  </Typography>
                </Grid2>
                <Grid2 container size="grow" justifyContent={'flex-end'}>
                  <DatePicker
                    onChange={date => {
                      if (isValid(date)) {
                        const validDate = date as Date;
                        handleChange('servicePeriodEnd', validDate);
                        update.mutateAsync({
                          id: invoice.id,
                          data: { servicePeriodEnd: endOfDay(validDate) }
                        });
                      } else if (date === null) {
                        handleChange('servicePeriodEnd', date);
                        update.mutateAsync({
                          id: invoice.id,
                          data: { servicePeriodEnd: date }
                        });
                      }
                    }}
                    value={values.servicePeriodEnd}
                    renderInput={props => <TextField {...props} size="small" />}
                    disabled={!editMode}
                    inputFormat="dd.MM.yyyy"
                  />
                </Grid2>
              </Grid2>
            </FormControl>
          </Grid2>
          <FormControl size="small" disabled={!editMode} required style={{ gap: 3 }}>
            <FormLabel sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <Typography variant="h6">
                {t(translations.pages.billing.detail.revenueType())}
              </Typography>
            </FormLabel>
            <Autocomplete
              options={revenueTypes?.data || []}
              getOptionLabel={customer => `${customer.revenueTypeNumber} ${customer.description}`}
              renderInput={params => <TextField {...params} />}
              size="small"
              onChange={(event, value) => {
                handleChange('revenueTypeVersionId', value?.id);
                handleChange('revenueType', value);
                update.mutateAsync({
                  id: invoice.id,
                  data: { revenueTypeVersionId: value?.id || null }
                });
              }}
              style={{ minWidth: '20em' }}
              value={values.revenueType || null}
              disabled={!editMode}
              loading={isLoading}
            />
          </FormControl>
          <FormLabel sx={{ display: 'inline-flex', alignItems: 'center' }} disabled={!editMode}>
            <Typography variant="h6">
              {t(translations.pages.billing.detail.costFields())}
            </Typography>
          </FormLabel>
          <Grid2 container justifyContent={'space-between'} spacing={2}>
            <Grid2 size={6}>
              <FormControl size="small" disabled={!editMode} fullWidth>
                <TextField
                  disabled={!editMode}
                  label={t(translations.pages.billing.detail.chapter())}
                  value={values.chapter}
                  size="small"
                  onChange={e => {
                    if (e.target.value.match(/^[0-9]*$/) || e.target.value === '') {
                      handleChange('chapter', e.target.value);
                    }
                  }}
                  slotProps={{ htmlInput: { maxLength: 5 } }}
                  onBlur={() =>
                    update.mutateAsync({
                      id: invoice.id,
                      data: { chapter: values.chapter }
                    })
                  }
                />
              </FormControl>
            </Grid2>
            <Grid2 size={6}>
              <FormControl size="small" disabled={!editMode} fullWidth>
                <TextField
                  disabled={!editMode}
                  label={t(translations.pages.billing.detail.title())}
                  value={values.title}
                  size="small"
                  onChange={e => handleChange('title', e.target.value)}
                  onBlur={() =>
                    update.mutateAsync({
                      id: invoice.id,
                      data: { title: values.title }
                    })
                  }
                />
              </FormControl>
            </Grid2>
          </Grid2>
          <Grid2 container justifyContent={'space-between'} spacing={2}>
            <Grid2 size={6}>
              <FormControl size="small" disabled={!editMode} fullWidth>
                <TextField
                  label={t(translations.pages.billing.detail.costBearer())}
                  value={values.costBearer}
                  disabled={!editMode}
                  size="small"
                  inputMode="numeric"
                  slotProps={{
                    htmlInput: {
                      sx: {
                        WebkitAppearance: 'none',
                        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                          display: 'none'
                        }
                      }
                    }
                  }}
                  onChange={e => {
                    if (e.target.value.match(/^[0-9]*$/) || e.target.value === '') {
                      handleChange('costBearer', e.target.value);
                    }
                  }}
                  onBlur={() =>
                    update.mutateAsync({
                      id: invoice.id,
                      data: { costBearer: values.costBearer }
                    })
                  }
                />
              </FormControl>
            </Grid2>
            <Grid2 size={6} alignItems={'center'} display={'flex'} gap={1}>
              <FormControl size="small" disabled fullWidth>
                <TextField
                  label={t(translations.pages.billing.detail.insArt())}
                  value={values.insArt}
                  disabled={!editMode}
                  size="small"
                  inputMode="numeric"
                  slotProps={{
                    htmlInput: {
                      sx: {
                        WebkitAppearance: 'none',
                        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                          display: 'none'
                        }
                      }
                    }
                  }}
                  onChange={e => {
                    if (e.target.value.match(/^[0-9]*$/) || e.target.value === '') {
                      handleChange('insArt', e.target.value);
                    }
                  }}
                  onBlur={e => {
                    update.mutateAsync({
                      id: invoice.id,
                      data: { insArt: e.target.value }
                    });
                  }}
                />
              </FormControl>

              <Tooltip title={t(translations.pages.billing.tooltips.costCentre())}>
                <HelpOutline fontSize="medium" style={{ opacity: 0.5 }} />
              </Tooltip>
            </Grid2>
          </Grid2>
        </Grid2>
        <Grid2
          container
          gap={2}
          margin={3}
          flexGrow={1}
          justifyContent={'space-between'}
          alignItems={'flex-start'}
        >
          <Grid2 container gap={1} alignItems={'center'}>
            <InvoiceStateChip
              state={getStateForChip(invoice.state)}
              cancelled={!!invoice.cancelledInvoiceId}
            />
          </Grid2>
          {canEditInvoice(user, invoice, permissions) && (
            <FormControlLabel
              label={t(translations.common.edit())}
              control={
                <Switch value={editMode} checked={editMode} onChange={handleEditModeSwitch} />
              }
              style={{ alignSelf: 'flex-start' }}
            />
          )}
        </Grid2>
        <Grid2 container gap={2} flexDirection={'column'} size={3}>
          {editMode ? (
            <InfoCustomerCard data={{ ...invoice.customer!, invoice, shouldHide: true }} />
          ) : (
            <Link
              to={`/billing/customers/${invoice.customer?.customerId}`}
              state={{ back: location.pathname }}
            >
              <InfoCustomerCard data={invoice.customer!} hideEdit isClickable />
            </Link>
          )}
          {shouldRenderActionButtons(user, invoice, permissions) &&
            (isFinanceUser(user, permissions) || canCreateInvoices(user, permissions)) && (
              <>
                {isPreviewEnabled && <PdfPreview onClick={() => handlePreview(invoice)} />}
                <ActionButtons />
              </>
            )}

          {isValidForm && !editMode && invoice.state === 'DRAFT' && (
            <Fade in={!editMode}>
              <Alert severity="info">
                <AlertTitle>
                  {t(translations.pages.billing.detail.readyForApprovalTitle())}
                </AlertTitle>
                {t(translations.pages.billing.detail.readyForApprovalMessage())}
              </Alert>
            </Fade>
          )}
          <Fade in={editMode}>
            <Alert severity="info">
              <AlertTitle>{t(translations.pages.billing.detail.editModeTitle())}</AlertTitle>
              {t(translations.pages.billing.detail.editModeMessage())}
            </Alert>
          </Fade>
          <Fade in={editMode}>
            <Grid2>{ErrorText(errors, invoice.state)}</Grid2>
          </Fade>
        </Grid2>
      </Grid2>
      <ArticleConfiguration invoice={invoice} editMode={editMode} ref={articleConfigurationRef} />
      <DenyDialog
        open={denyDialogOpen}
        handleClose={() => {
          setDenyDialogOpen(false);
          handleChange('cancellationReason', '');
        }}
        onConfirm={denyInvoice}
        cancellationReason={values.cancellationReason}
        setCancellationReason={(cancellationReason: string) =>
          handleChange('cancellationReason', cancellationReason)
        }
      />
      <ApproveDialog
        open={approveDialogOpen}
        handleClose={() => setApproveDialogOpen(false)}
        onConfirm={handleApproveInvoice}
      />
      <ApproveSplitDialog
        open={approveSplitDialogOpen}
        handleClose={() => setApproveSplitDialogOpen(false)}
        onConfirm={handleSplitInvoice}
        invoice={invoice}
      />
      <CancelDialog
        open={cancelDialogOpen}
        handleClose={() => setCancelDialogOpen(false)}
        onConfirm={handleCancelInvoice}
      />
      <DeleteDialog
        open={deleteDialogOpen}
        handleClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteInvoice}
      />
    </div>
  );
};

//TODO make this to a reusable component
const ErrorText = (errors: FormikErrors<Invoice>, state: keyof typeof State) => {
  const { t } = useTranslation();

  if (!errors || Object.entries(errors).length === 0) {
    return <></>;
  }

  return (
    <Alert severity="warning">
      <b>
        {state === State.DRAFT
          ? t(translations.pages.billing.formikErrors.labelMarkREADY())
          : t(translations.pages.billing.formikErrors.labelMarkAPPROVE())}
      </b>
      <List dense>
        {Object.entries(errors).map(([key, value]) =>
          typeof value === 'string' ? (
            <ListItem key={key} disablePadding>
              <ListItemText
                primary={t(translations.pages.billing.formikErrors.keys[key]())}
                secondary={`${JSON.stringify(value) || value}`}
              />
            </ListItem>
          ) : Array.isArray(value) ? (
            value.map((error, index) => (
              <ListItem key={`${key}-${index}`} disablePadding>
                <ListItemText
                  primary={t(translations.pages.billing.formikErrors.keys[key]())}
                  secondary={`${JSON.stringify(error) || error}`}
                />
              </ListItem>
            ))
          ) : (
            Object.entries(value).map(([innerKey, error]) => (
              <ListItem key={key} disablePadding>
                <ListItemText
                  key={innerKey}
                  primary={t(translations.pages.billing.formikErrors.keys[key][innerKey]())}
                  secondary={`${JSON.stringify(error) || error}`}
                />
              </ListItem>
            ))
          )
        )}
      </List>
    </Alert>
  );
};
